import React from "react";
import { CheckCircle, DeleteOutline, Error as ErrorIcon, RadioButtonUnchecked } from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import { Gate, OPEN } from "../gating";
import { BASE_DATA_LAYERS, BaseDataLayerKey } from "../layers";
import type { StatusLine, StatusLineStatus } from "../model";

interface StatusCardProps {
  layerKey: BaseDataLayerKey;
  /** What the row contains (see `BaseDataRowModel.lines`). */
  lines: readonly StatusLine[];
  /** Whether the row has data to delete. */
  hasData: boolean;
  /** Whether the row can be deleted now (see `BaseDataGating.rows`). */
  deleteGate?: Gate;
  /** Deletes every planner layer of the row. */
  onDelete(): void;
}

const LineIcon: React.FC<{ status: StatusLineStatus }> = ({ status }) => {
  switch (status) {
    case "done":
      return <CheckCircle sx={{ color: (theme) => theme.palette.success.main, fontSize: 20 }} />;
    case "missing":
      return <ErrorIcon sx={{ color: (theme) => theme.palette.warning.main, fontSize: 20 }} />;
    case "required":
      return <ErrorIcon sx={{ color: (theme) => theme.palette.biarri.secondary.inactiveGrey, fontSize: 20 }} />;
    default:
      return <RadioButtonUnchecked sx={{ color: (theme) => theme.palette.biarri.secondary.inactiveGrey, fontSize: 20 }} />;
  }
};

/**
 * What a base data row contains, one line per planner layer, with a Delete for the whole row.
 */
const StatusCard: React.FC<StatusCardProps> = ({ layerKey, lines, hasData, deleteGate = OPEN, onDelete }) => {
  const deleteLabel = `Delete ${BASE_DATA_LAYERS[layerKey].noun}`;

  return (
    <Box display="flex" alignItems="center" gap={1.25} border={1} borderColor="divider" borderRadius={1} pl={1.5} pr={0.75} py={0.75}>
      <Box component="ul" flexGrow={1} minWidth={0} display="grid" gap={0.5} m={0} p={0} sx={{ listStyle: "none" }}>
        {lines.map(({ key, status, text, subtitle }) => (
          <Box component="li" key={key} display="flex" alignItems="center" gap={1.25}>
            <LineIcon status={status} />
            <Box minWidth={0}>
              <Typography variant="body2" color={status === "done" ? undefined : "text.secondary"}>
                {text}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      <Tooltip title={hasData ? (deleteGate.reason ?? deleteLabel) : ""}>
        <span>
          <IconButton aria-label={deleteLabel} color="error" size="small" disabled={!deleteGate.enabled || !hasData} onClick={onDelete}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default StatusCard;
